import React, { useEffect, useMemo, useRef, useState } from "react";
import { Typography } from "@mui/material";
import "./uploadVideoPreview.scss";
import CancelMeasurementButton from "../userDetail/testModule/CancelMeasurementButton";
import closeIcon from "../../assets/images/closeIcon.svg";
import { useSection } from "../../i18n/useSection";

/**
 * Shared confirmation step for a video picked from the device gallery.
 * Shows the selected video (with native playback controls) so the user can
 * check it before it is uploaded.
 */
const UploadVideoPreview = ({
  file,
  onConfirm,
  onCancel,
  title,
  note,
  confirmLabel,
  variant,
  moduleTitle,
}) => {
  const t = useSection("uploadVideoPreview");
  const [confirming, setConfirming] = useState(false);
  const [poster, setPoster] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    setPoster(null);
    setPlaying(false);
    setPreviewError(false);
    setConfirming(false);
  }, [file]);

  const captureThumbnail = () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight) return;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = Math.min(video.videoWidth, 960);
      canvas.height = Math.round(
        (canvas.width * video.videoHeight) / video.videoWidth,
      );
      const context = canvas.getContext("2d");
      if (!context) return;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      setPoster(canvas.toDataURL("image/jpeg", 0.85));
    } catch {
      // Native playback remains available if the device cannot capture a frame.
    }
  };

  const playPreview = async () => {
    try {
      await videoRef.current?.play();
    } catch {
      // Keep native controls and the play action available to retry.
      setPlaying(false);
    }
  };

  const videoUrl = useMemo(
    () => (file ? URL.createObjectURL(file) : null),
    [file],
  );

  useEffect(
    () => () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    },
    [videoUrl],
  );

  if (!file || !videoUrl) return null;

  const handleConfirm = () => {
    if (confirming) return;
    setConfirming(true);
    onConfirm?.(file);
  };

  return (
    <div
      className={`upload-video-preview-wrapper measurement-video-preview${variant === "sts" ? " embedded" : ""}`}
    >
      {moduleTitle && <h1 className="preview-module-title">{moduleTitle}</h1>}
      <div className="upload-video-preview-header">
        <Typography className="title">{title || t?.title}</Typography>
        <button
          type="button"
          className="close-btn"
          onClick={onCancel}
          aria-label={t?.close}
        >
          <img src={closeIcon} alt="" className="close-icon" />
        </button>
      </div>

      <div className="upload-video-preview-content">
        {/* `#t=0.1` makes iOS Safari render the first frame instead of a
            blank box before playback starts. */}
        <video
          ref={videoRef}
          src={`${videoUrl}#t=0.1`}
          poster={poster || undefined}
          onLoadedData={captureThumbnail}
          onSeeked={captureThumbnail}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onError={() => setPreviewError(true)}
          className="video-preview"
          playsInline
          controls
          preload="auto"
        />
        {!playing && !previewError && (
          <button
            type="button"
            className="preview-play-btn"
            onClick={playPreview}
            aria-label={t.play}
            title={t.play}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M8 5.5a1 1 0 0 1 1.52-.85l9.5 6a1.6 1.6 0 0 1 0 2.7l-9.5 6A1 1 0 0 1 8 18.5z" />
            </svg>
          </button>
        )}
        {previewError && (
          <div className="preview-error" role="status">
            <p>{t.unavailable}</p>
            <p>{file.name}</p>
          </div>
        )}
      </div>

      {note && (
        <Typography
          className="upload-preview-note"
          sx={{
            px: 3,
            pb: 2,
            color: "#2B303B",
            flexShrink: 0,
          }}
        >
          {note}
        </Typography>
      )}

      <div className="upload-video-preview-controls">
        <button
          type="button"
          className="upload-btn"
          onClick={handleConfirm}
          disabled={confirming}
        >
          {confirmLabel || t?.upload}
        </button>
        <CancelMeasurementButton />
      </div>
    </div>
  );
};

export default UploadVideoPreview;
